import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import NewNleEditor from './NewNleEditor';
import SmoothScaleCarousel from './SmoothScaleCarousel';
import VideoGenerationSteps from './VideoGenerationSteps';
import { useDarkMode } from '../DarkModeContext';
import { trackEvent } from '../utils/amplitude';
import {
  buildLocalRawMoments,
  buildPendingCutWindowsByVideo,
  probeVideoDuration,
  toggleLocalMomentHidden,
} from '../utils/localRawMoments';
import { isRawVideoTooLong, RAW_VIDEO_TOO_LONG_MESSAGE } from '../utils/rawVideoLimits';

const REFERRAL_OPTIONS = [
  'TikTok',
  'Instagram',
  'X (Twitter)',
  'Reddit',
  'LinkedIn',
  'YouTube',
  'AI',
  'Friend or Colleague',
  'Other',
];

const POSTING_OPTIONS = [
  '1–2 times',
  '3–4 times',
  '5–7 times',
  'Every day',
  'Not sure yet',
];

const PROMO_OPTIONS = [
  { id: 'personal_brand', label: 'Personal brand', subtitle: 'You, your story, your audience' },
  { id: 'brand', label: 'Brand', subtitle: 'A product, company, or offer' },
];

const B_ROLL_EXAMPLES = [
  'Pouring coffee or making breakfast',
  'Walking down the street or into a café',
  'Typing on a laptop or scrolling on your phone',
  'Working out, driving, or a sunset timelapse',
];

const BRIAN_VOICE_ID = 'nPczCjzI2devNBz1zQrb';

function getScriptSentenceGroups(text) {
  if (!text || typeof text !== 'string') return [];
  const sentences = text.split(/([.!?]+[\s\n]*)/).filter((s) => s.trim());
  const groups = [];
  let current = [];
  for (let i = 0; i < sentences.length; i++) {
    if (sentences[i].match(/[.!?]/)) {
      current.push(sentences[i]);
      groups.push([...current]);
      current = [];
    } else {
      current.push(sentences[i]);
    }
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

/** Stacked portrait cards + plus — clear tappable import CTA (not an image). */
function ImportStackButton({ onClick, disabled, busy, size = 'grid', label = 'Import videos' }) {
  const isHero = size === 'hero';
  const cardW = isHero ? 100 : 78;
  const cardH = cardW * (4 / 3);
  const plus = isHero ? 26 : 20;
  const stroke = isHero ? 3.5 : 3;
  const radius = cardW * 0.22;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: isHero ? 'auto' : '100%',
        height: isHero ? 'auto' : '100%',
        minHeight: isHero ? undefined : 160,
        padding: isHero ? '22px 28px 18px' : '14px 8px 12px',
        borderRadius: isHero ? 16 : 12,
        border: '1.5px dashed rgba(225,246,121,0.55)',
        background: 'rgba(225,246,121,0.08)',
        cursor: disabled || busy ? 'default' : 'pointer',
        opacity: disabled || busy ? 0.55 : 1,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: cardW * 1.35,
          height: cardH * 1.15,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: cardW,
            height: cardH,
            borderRadius: radius,
            background: '#3A3D46',
            border: '1.5px solid rgba(255,255,255,0.22)',
            transform: 'rotate(-14deg) translate(-10px, -4px)',
            opacity: 0.92,
          }}
        />
        <div
          style={{
            position: 'absolute',
            width: cardW,
            height: cardH,
            borderRadius: radius,
            background: '#3A3D46',
            border: '1.5px solid rgba(255,255,255,0.22)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {busy ? (
            <span style={{ color: '#fff', fontSize: 12 }}>…</span>
          ) : (
            <span style={{ position: 'relative', width: plus, height: plus, display: 'inline-block' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: (plus - stroke) / 2,
                  width: plus,
                  height: stroke,
                  borderRadius: stroke / 2,
                  background: '#fff',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  left: (plus - stroke) / 2,
                  top: 0,
                  width: stroke,
                  height: plus,
                  borderRadius: stroke / 2,
                  background: '#fff',
                }}
              />
            </span>
          )}
        </div>
      </div>
      <span
        style={{
          marginTop: isHero ? 16 : 12,
          background: '#e1f679',
          color: '#0B0E16',
          fontWeight: 700,
          fontSize: isHero ? 15 : 13,
          padding: isHero ? '12px 22px' : '8px 14px',
          borderRadius: isHero ? 10 : 8,
          minWidth: isHero ? 160 : 88,
          textAlign: 'center',
        }}
      >
        {busy ? '…' : label}
      </span>
    </button>
  );
}

/** Random 2s looping clip — plays only while in the viewport. */
function MomentVideo({ moment }) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsActive(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsActive(Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.35));
      },
      { threshold: [0, 0.35, 0.6] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !isActive || moment.hidden) {
      try {
        el?.pause?.();
      } catch (_) {}
      return undefined;
    }
    const start = moment.startSec || 0;
    const end = moment.endSec || start + 2;
    const onTime = () => {
      if (el.currentTime >= end - 0.05 || el.currentTime < start - 0.1) {
        el.currentTime = start;
        el.play().catch(() => {});
      }
    };
    const onReady = () => {
      el.currentTime = start;
      el.play().catch(() => {});
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadeddata', onReady);
    if (el.readyState >= 2) onReady();
    else el.play().catch(() => {});
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadeddata', onReady);
      try {
        el.pause();
      } catch (_) {}
    };
  }, [isActive, moment.hidden, moment.startSec, moment.endSec, moment.previewUrl]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', background: '#000' }}>
      {isActive && !moment.hidden ? (
        <video
          ref={videoRef}
          src={moment.previewUrl}
          muted
          playsInline
          preload="metadata"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: moment.hidden ? 0.4 : 1,
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            background: '#1a1f2e',
            opacity: moment.hidden ? 0.4 : 1,
          }}
        />
      )}
    </div>
  );
}

const STEP = {
  REFERRAL: 0,
  POSTING: 1,
  PROMO: 2,
  BIO: 3,
  INTRO: 4,
  BROLL_KNOW: 5,
  BROLL_EXPLAIN: 6,
  UPLOAD: 7,
  SELECT_CUTS: 8,
  TOPIC: 9,
  HAS_SCRIPT: 10,
  SCRIPT_PASTE: 11,
  SCRIPT_AI: 12,
  VOICE: 13,
  RESULT: 14,
  COMPLETE: 15,
};

/**
 * Non-skippable fullscreen onboarding overlay (web).
 * Shown while onboardingCompleted === false.
 */
export default function OnboardingOverlay({
  open,
  workspaceId,
  workspace,
  onReferralSourceSaved,
  onComplete,
}) {
  const { darkMode } = useDarkMode();
  const [step, setStep] = useState(STEP.REFERRAL);
  const [referral, setReferral] = useState('');
  const [posting, setPosting] = useState('');
  const [promoTypes, setPromoTypes] = useState(() => new Set());
  const [bio, setBio] = useState(workspace?.profile?.currentSituation || '');
  const [knowsBroll, setKnowsBroll] = useState(null);
  const [pickedFiles, setPickedFiles] = useState([]);
  const [localMoments, setLocalMoments] = useState([]);
  const [topic, setTopic] = useState('');
  const [hasScript, setHasScript] = useState(null);
  const [scriptContent, setScriptContent] = useState('');
  const [script, setScript] = useState(null);
  const [scriptVersion, setScriptVersion] = useState('main');
  const [generatingScript, setGeneratingScript] = useState(false);
  const [isEditingScript, setIsEditingScript] = useState(false);
  const [voiceoverUrl, setVoiceoverUrl] = useState(null);
  const [voiceoverTimestamps, setVoiceoverTimestamps] = useState(null);
  const [generatingVo, setGeneratingVo] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoSaved, setVideoSaved] = useState(false);
  const [savingVideo, setSavingVideo] = useState(false);
  const [showNle, setShowNle] = useState(false);
  const [nleTimelineCuts, setNleTimelineCuts] = useState([]);
  const [nleCutsLoading, setNleCutsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cuts, setCuts] = useState([]);
  const fileInputRef = useRef(null);
  const uploadStartedRef = useRef(false);
  const pendingCutWindowsByUriRef = useRef(new Map());
  const extractTriggeredVideoIdsRef = useRef(new Set());
  /** Stable File → previewUrl mapping for uploads still in flight */
  const filePreviewRef = useRef(new WeakMap());
  const videoPollRef = useRef(null);
  const videoGenStartedRef = useRef(false);
  const pickedFilesRef = useRef(pickedFiles);
  const cutsPollIntervalRef = useRef(null);
  pickedFilesRef.current = pickedFiles;

  const bg = darkMode ? '#0f1219' : '#f5f6f8';
  const panel = darkMode ? '#161b27' : '#fff';
  const text = darkMode ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.92)';
  const muted = darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)';
  const border = darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)';

  const canNext = useMemo(() => {
    switch (step) {
      case STEP.REFERRAL: return !!referral;
      case STEP.POSTING: return !!posting;
      case STEP.PROMO: return promoTypes.size > 0;
      case STEP.BIO: return (bio || '').trim().length > 0;
      case STEP.BROLL_KNOW: return knowsBroll !== null;
      case STEP.UPLOAD: return localMoments.length > 0 || pickedFiles.length > 0;
      case STEP.SELECT_CUTS: return localMoments.some((m) => !m.hidden);
      case STEP.TOPIC: return (topic || '').trim().length > 0;
      case STEP.HAS_SCRIPT: return hasScript !== null;
      case STEP.SCRIPT_PASTE:
      case STEP.SCRIPT_AI: return (scriptContent || '').trim().length > 0 && !generatingScript;
      case STEP.VOICE: return !!voiceoverUrl && !generatingVo;
      case STEP.RESULT: return !!generatedVideoUrl && !generatingVideo;
      default: return true;
    }
  }, [step, referral, posting, promoTypes, bio, knowsBroll, localMoments, pickedFiles, topic, hasScript, scriptContent, generatingScript, voiceoverUrl, generatingVo, generatedVideoUrl, generatingVideo]);

  const parseCutsResponse = useCallback((data) => {
    if (Array.isArray(data)) return data;
    return data?.items || data?.cuts || [];
  }, []);

  const pollCuts = useCallback(async () => {
    if (!workspaceId) return [];
    const ids = [
      ...pickedFilesRef.current.map((p) => p.videoId).filter(Boolean),
      ...((pendingCutWindowsByUriRef.current && [...pendingCutWindowsByUriRef.current.values()]) || [])
        .map((e) => e?.videoId)
        .filter(Boolean),
    ].map(String);
    const uniqueIds = [...new Set(ids)];
    try {
      let items = [];
      if (uniqueIds.length > 0) {
        const res = await api.getWorkspaceCuts(workspaceId, { videoIds: uniqueIds.join(',') });
        items = parseCutsResponse(res?.data);
      } else {
        // Uploads may still be linking videoIds — fall back to recent workspace cuts
        const res = await api.getWorkspaceCuts(workspaceId, { page: 1, pageSize: 40 });
        items = parseCutsResponse(res?.data);
      }
      const visible = (items || []).filter((c) => c && !c.hidden && c.url);
      if (visible.length) setCuts(visible);
      return visible;
    } catch (_) {
      return [];
    }
  }, [workspaceId, parseCutsResponse]);

  const startCutsPolling = useCallback(() => {
    if (cutsPollIntervalRef.current) return;
    pollCuts();
    cutsPollIntervalRef.current = setInterval(() => {
      pollCuts();
    }, 3000);
    setTimeout(() => {
      if (cutsPollIntervalRef.current) {
        clearInterval(cutsPollIntervalRef.current);
        cutsPollIntervalRef.current = null;
      }
    }, 180000);
  }, [pollCuts]);

  useEffect(() => () => {
    if (cutsPollIntervalRef.current) {
      clearInterval(cutsPollIntervalRef.current);
      cutsPollIntervalRef.current = null;
    }
  }, []);

  const uploadFilesInBackground = useCallback(async (files) => {
    if (!workspaceId || !files?.length) return;
    for (const item of files) {
      const file = item.file;
      if (!file) continue;
      const previewKey = item.previewUrl || '';
      try {
        const urlRes = await api.getWorkspaceUploadUrl(workspaceId, file.name, file.type || 'video/mp4', file.size);
        const { uploadUrl, key, videoUrl } = urlRes.data || urlRes;
        await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'video/mp4' } });

        const pending = pendingCutWindowsByUriRef.current.get(previewKey);
        const windows = pending?.windows?.length ? pending.windows : null;
        const confirm = await api.confirmWorkspaceUpload(
          workspaceId,
          key,
          videoUrl,
          file.name,
          '',
          [],
          {
            ...(windows ? { pendingCutWindows: windows } : { skipAutoCuts: true }),
            ...(Number.isFinite(item.duration) ? { durationSec: item.duration } : {}),
          }
        );
        const video = confirm?.data?.video || confirm?.video;
        if (video?._id) {
          const videoId = String(video._id);
          setPickedFiles((prev) =>
            prev.map((p) => (p.file === file ? { ...p, videoId } : p))
          );
          setLocalMoments((prev) =>
            prev.map((m) =>
              m.previewUrl === previewKey && !m.videoId
                ? { ...m, videoId }
                : m
            )
          );
          if (pending) {
            pendingCutWindowsByUriRef.current.set(previewKey, { ...pending, videoId });
          }
          if (windows?.length) {
            extractTriggeredVideoIdsRef.current.add(videoId);
          } else if (pending?.windows?.length && !extractTriggeredVideoIdsRef.current.has(videoId)) {
            extractTriggeredVideoIdsRef.current.add(videoId);
            api.extractWorkspaceCutWindows(workspaceId, videoId, pending.windows).catch((err) => {
              console.warn('[OnboardingOverlay] extract after confirm failed', err);
              extractTriggeredVideoIdsRef.current.delete(videoId);
            });
          }
          // Video is linked — start/keep polling for extracted cuts
          startCutsPolling();
        }
      } catch (err) {
        console.warn('[OnboardingOverlay] upload failed', err);
      }
    }
  }, [workspaceId, startCutsPolling]);

  const handlePickFiles = useCallback(async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const probed = await Promise.all(
      list.map(async (file) => {
        const previewUrl = URL.createObjectURL(file);
        filePreviewRef.current.set(file, previewUrl);
        const duration = await probeVideoDuration(previewUrl);
        return { file, previewUrl, duration };
      })
    );
    const rejected = probed.filter((item) => isRawVideoTooLong(item.duration));
    const mapped = probed.filter((item) => !isRawVideoTooLong(item.duration));
    rejected.forEach((item) => {
      URL.revokeObjectURL(item.previewUrl);
      filePreviewRef.current.delete(item.file);
    });
    if (rejected.length) {
      setError(rejected.length === probed.length
        ? RAW_VIDEO_TOO_LONG_MESSAGE
        : `${rejected.length} video${rejected.length === 1 ? '' : 's'} skipped. ${RAW_VIDEO_TOO_LONG_MESSAGE}`);
    }
    if (!mapped.length) {
      e.target.value = '';
      return;
    }
    setPickedFiles((prev) => [...prev, ...mapped]);
    const newMoments = buildLocalRawMoments(
      mapped.map((m) => ({
        previewUrl: m.previewUrl,
        duration: m.duration,
        fileName: m.file?.name,
        videoId: m.videoId,
      }))
    );
    setLocalMoments((prev) => [...newMoments, ...prev]);
    setStep(STEP.SELECT_CUTS);
    uploadFilesInBackground(mapped);
    e.target.value = '';
  }, [uploadFilesInBackground]);

  const generateAiScript = useCallback(async () => {
    if (!workspaceId) return;
    setGeneratingScript(true);
    setIsEditingScript(false);
    setError('');
    try {
      await api.generateWorkspaceScriptsAsync(workspaceId, {
        hooks: [`Something most people don't realize about ${topic || 'this'} is…`],
        includePublishedContext: false,
        creativityLevel: 3,
        duration: 20,
        language: 'en',
        topic: topic || undefined,
      });
      const start = Date.now();
      const tryScripts = async () => {
        const list = await api.getWorkspaceScripts(workspaceId, { type: 'voiceover', lite: true, limit: 5 });
        const scripts = list?.data?.scripts || list?.data || [];
        const s = Array.isArray(scripts) ? scripts[0] : null;
        if (s) {
          setScript(s);
          setScriptContent(s.content || '');
          setGeneratingScript(false);
          return true;
        }
        return false;
      };
      const poll = async () => {
        try {
          const status = await api.getWorkspaceScriptsStatus(workspaceId);
          if (status?.data?.status === 'completed' && (await tryScripts())) return;
        } catch (_) {}
        if (await tryScripts()) return;
        if (Date.now() - start > 90000) {
          setGeneratingScript(false);
          setError('Script generation timed out. You can paste a script instead.');
          return;
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    } catch (err) {
      setGeneratingScript(false);
      setError(err?.response?.data?.message || err?.message || 'Could not generate script');
    }
  }, [workspaceId, topic]);

  useEffect(() => {
    if (step === STEP.SCRIPT_AI && !script && !generatingScript && !scriptContent) {
      generateAiScript();
    }
  }, [step, script, generatingScript, scriptContent, generateAiScript]);

  const ensureScriptSaved = useCallback(async () => {
    if (!workspaceId || !(scriptContent || '').trim()) return null;
    if (script?._id) {
      const version = scriptVersion === 'alt' || scriptVersion === 'third' ? scriptVersion : 'main';
      await api.updateWorkspaceScriptContent(workspaceId, script._id, version, scriptContent);
      return script;
    }
    const res = await api.saveCustomScript(workspaceId, {
      content: scriptContent,
      type: 'voiceover',
      title: topic || 'Onboarding script',
    });
    const s = res?.data?.script || res?.script || res?.data;
    if (s) setScript(s);
    return s;
  }, [workspaceId, scriptContent, script, scriptVersion, topic]);

  const generateVoiceover = useCallback(async () => {
    if (!workspaceId || generatingVo) return;
    setGeneratingVo(true);
    setError('');
    try {
      const s = await ensureScriptSaved();
      const scriptId = s?._id || script?._id;
      if (!scriptId) throw new Error('Save a script first');
      const res = await api.generateWorkspaceVoiceover(workspaceId, scriptId, {
        voiceId: BRIAN_VOICE_ID,
        content: scriptContent,
      });
      const url = res?.data?.voiceoverUrl || res?.voiceoverUrl;
      if (!url) {
        throw new Error(res?.data?.message || res?.data?.error || 'No voiceover URL returned');
      }
      const timestamps = res?.data?.voiceoverTimestamps || res?.voiceoverTimestamps || null;
      setVoiceoverUrl(url);
      if (timestamps) setVoiceoverTimestamps(timestamps);
      if (res?.data?.script || res?.script) {
        const nextScript = { ...(res.data?.script || res.script), voiceoverUrl: url };
        if (timestamps) nextScript.voiceoverTimestamps = timestamps;
        setScript((prev) => ({ ...(prev || {}), ...nextScript }));
      } else {
        setScript((prev) => (prev
          ? { ...prev, voiceoverUrl: url, ...(timestamps ? { voiceoverTimestamps: timestamps } : {}) }
          : prev));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Voiceover failed';
      setError(typeof msg === 'string' ? msg : 'Voiceover failed');
    } finally {
      setGeneratingVo(false);
    }
  }, [ensureScriptSaved, generatingVo, script, scriptContent, workspaceId]);

  const stopVideoPoll = useCallback(() => {
    if (videoPollRef.current) {
      clearInterval(videoPollRef.current);
      videoPollRef.current = null;
    }
  }, []);

  const publishOnboardingVideo = useCallback(async (videoUrlOverride = null, { force = false } = {}) => {
    const url = videoUrlOverride || generatedVideoUrl;
    const scriptId = script?._id;
    if (!workspaceId || !scriptId || !url) return false;
    if (videoSaved && !force) return true;
    setSavingVideo(true);
    try {
      const response = await api.publishWorkspaceVideo(workspaceId, scriptId, {
        videoUrl: url,
      });
      const msg = response?.data?.message || response?.message || '';
      if (msg === 'Video published successfully' || msg === 'Video already published') {
        setVideoSaved(true);
        trackEvent('web_onboarding_video_saved', { workspaceId, scriptId });
        return true;
      }
      // Some backends return the published record without that exact message
      if (response?.data && !response?.data?.error) {
        setVideoSaved(true);
        trackEvent('web_onboarding_video_saved', { workspaceId, scriptId });
        return true;
      }
      return false;
    } catch (err) {
      console.warn('[OnboardingOverlay] publish failed', err);
      return false;
    } finally {
      setSavingVideo(false);
    }
  }, [workspaceId, script?._id, generatedVideoUrl, videoSaved]);

  const downloadOnboardingVideo = useCallback(async () => {
    if (!generatedVideoUrl) return;
    try {
      const words = String(scriptContent || '')
        .replace(/[\r\n]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 6)
        .join(' ')
        .replace(/[/\\?%*:|"<>]/g, '')
        .replace(/[^a-zA-Z0-9\s_-]/g, '')
        .trim()
        .replace(/[\s_]+/g, '-')
        .toLowerCase()
        .slice(0, 48) || 'video';
      const filename = `merra-${words}-${Date.now()}.mp4`;
      let directUrl = generatedVideoUrl;
      if (script?._id && workspaceId) {
        try {
          const downloadResp = await api.getWorkspaceStudioVideoDownloadUrl(workspaceId, script._id, {
            params: { filename },
          });
          directUrl =
            downloadResp?.data?.downloadUrl
            || downloadResp?.data?.url
            || downloadResp?.data
            || generatedVideoUrl;
        } catch (_) {
          /* use generated url */
        }
      }
      const link = document.createElement('a');
      link.href = directUrl;
      link.download = filename;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      trackEvent('web_onboarding_video_download', { workspaceId, scriptId: script?._id });
    } catch (err) {
      setError(err?.message || 'Download failed');
    }
  }, [generatedVideoUrl, scriptContent, script?._id, workspaceId]);

  const generateVideo = useCallback(async () => {
    if (!workspaceId || !script?._id || generatingVideo) return;
    stopVideoPoll();
    videoGenStartedRef.current = true;
    setVideoSaved(false);
    setGeneratingVideo(true);
    setVideoProgress(5);
    setError('');
    try {
      // Ensure cut pool is loaded for carousel + orderedCutIds
      startCutsPolling();
      const freshCuts = await pollCuts();
      const cutPool = (freshCuts?.length ? freshCuts : cuts).filter((c) => c && !c.hidden && c._id);

      let musicIdToUse = '';
      try {
        const musicResponse = await api.getWorkspaceMusic(workspaceId);
        const availableMusic = musicResponse?.data || [];
        if (Array.isArray(availableMusic) && availableMusic.length > 0) {
          musicIdToUse = availableMusic[Math.floor(Math.random() * availableMusic.length)]._id;
        }
      } catch (_) {
        /* optional */
      }

      const orderedCutIds = cutPool.map((c) => c._id);
      const timestamps = voiceoverTimestamps || script?.voiceoverTimestamps || null;
      await api.generateWorkspaceVideo(workspaceId, script._id, {
        voiceoverUrl,
        voiceoverTimestamps: timestamps || undefined,
        orderedCutIds: orderedCutIds.length ? orderedCutIds : undefined,
        musicId: musicIdToUse || undefined,
        enableSubtitles: true,
      });

      const scriptIdForPolling = script._id;
      const start = Date.now();
      const TIMEOUT_MS = 10 * 60 * 1000;
      videoPollRef.current = setInterval(async () => {
        try {
          if (Date.now() - start > TIMEOUT_MS) {
            stopVideoPoll();
            setGeneratingVideo(false);
            setVideoProgress(0);
            setError('Video generation timed out.');
            return;
          }
          const statusResp = await api.getWorkspaceVideoStatus(workspaceId, scriptIdForPolling);
          const { status, progress, videoUrl } = statusResp?.data || {};
          setVideoProgress(progress || 0);
          if ((status === 'draft' && videoUrl) || status === 'error') {
            stopVideoPoll();
            setGeneratingVideo(false);
            if (status === 'draft' && videoUrl) {
              setGeneratedVideoUrl(videoUrl);
              setVideoProgress(100);
              trackEvent('web_onboarding_video_generated', { workspaceId, scriptId: scriptIdForPolling });
              try {
                const resp = await api.getWorkspaceScript(workspaceId, scriptIdForPolling);
                const updated = resp?.data?.script || resp?.script || resp?.data;
                if (updated) {
                  setScript((prev) => ({ ...(prev || {}), ...updated }));
                  if (updated.voiceoverTimestamps) setVoiceoverTimestamps(updated.voiceoverTimestamps);
                }
              } catch (_) {
                /* ignore */
              }
              // Auto-save to Generated Videos as soon as the minivlog is ready
              publishOnboardingVideo(videoUrl, { force: true }).catch(() => {});
            } else {
              setVideoProgress(0);
              setError('Video generation failed.');
            }
          }
        } catch (pollErr) {
          console.error('[OnboardingOverlay] video status poll failed', pollErr);
        }
      }, 2000);
    } catch (err) {
      stopVideoPoll();
      setGeneratingVideo(false);
      setVideoProgress(0);
      const msg =
        err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Video generation failed';
      setError(typeof msg === 'string' ? msg : 'Video generation failed');
    }
  }, [
    workspaceId,
    script,
    generatingVideo,
    cuts,
    voiceoverUrl,
    voiceoverTimestamps,
    stopVideoPoll,
    pollCuts,
    startCutsPolling,
    publishOnboardingVideo,
  ]);

  // Auto-start video generation when landing on "Your minivlog"
  useEffect(() => {
    if (step !== STEP.RESULT) return undefined;
    startCutsPolling();
    if (generatedVideoUrl || generatingVideo || videoGenStartedRef.current) return undefined;
    if (!script?._id || !voiceoverUrl) return undefined;
    generateVideo();
    return undefined;
  }, [step, generatedVideoUrl, generatingVideo, script?._id, voiceoverUrl, generateVideo, startCutsPolling]);

  useEffect(() => () => stopVideoPoll(), [stopVideoPoll]);

  const openOnboardingNleEditor = useCallback(async () => {
    if (!workspaceId) return;
    setNleTimelineCuts([]);
    setNleCutsLoading(true);
    setShowNle(true);
    try {
      let current = script;
      if (script?._id) {
        try {
          const res = await api.getWorkspaceScript(workspaceId, script._id);
          current = res?.data?.script || res?.script || res?.data || script;
          if (current) {
            setScript((prev) => ({ ...(prev || {}), ...current }));
            if (current.voiceoverTimestamps) setVoiceoverTimestamps(current.voiceoverTimestamps);
            if (current.voiceoverUrl) setVoiceoverUrl(current.voiceoverUrl);
            if (current.content && !scriptContent) setScriptContent(current.content);
          }
        } catch (_) {}
      }

      // Refresh available cuts (uploads / extract may have finished since last poll)
      const freshCuts = await pollCuts();
      const pool = (freshCuts?.length ? freshCuts : cuts).filter((c) => c && !c.hidden && c.url);

      const ids = (current?.usedCutIds || []).map((id) => String(id));
      if (ids.length > 0) {
        const response = await api.getWorkspaceCuts(workspaceId, {
          ids: ids.join(','),
        });
        const items = parseCutsResponse(response?.data);
        const found = new Map(items.filter((c) => !c.hidden).map((c) => [String(c._id), c]));
        const ordered = ids.map((id) => found.get(String(id))).filter(Boolean);
        if (ordered.length > 0) {
          setNleTimelineCuts(ordered);
        } else {
          setNleTimelineCuts(pool);
        }
      } else {
        setNleTimelineCuts(pool);
      }
    } catch (err) {
      console.warn('[OnboardingOverlay] open NLE failed', err);
      setNleTimelineCuts(cuts.filter((c) => !c.hidden && c.url));
    } finally {
      setNleCutsLoading(false);
    }
  }, [workspaceId, script, cuts, scriptContent, pollCuts, parseCutsResponse]);

  const finish = useCallback(async () => {
    setBusy(true);
    try {
      if (generatedVideoUrl && !videoSaved) {
        await publishOnboardingVideo(generatedVideoUrl);
      }
      await api.completeOnboarding();
      trackEvent('web_onboarding_completed', { workspaceId });
      onComplete?.({
        workspaceId,
        scriptId: script?._id || null,
        videoUrl: generatedVideoUrl || null,
        videoSaved: true,
      });
    } catch (err) {
      setError(err?.message || 'Could not complete onboarding');
    } finally {
      setBusy(false);
    }
  }, [workspaceId, onComplete, generatedVideoUrl, videoSaved, publishOnboardingVideo, script?._id]);

  const goNext = useCallback(async () => {
    setError('');
    if (step === STEP.REFERRAL) {
      setBusy(true);
      try {
        await api.updateOnboardingAnswers({ referralSource: referral });
        onReferralSourceSaved?.(referral);
        setStep(STEP.POSTING);
      } catch (err) {
        setError(err?.message || 'Could not save');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === STEP.POSTING) {
      await api.updateOnboardingAnswers({ postingFrequency: posting }).catch(() => {});
      setStep(STEP.PROMO);
      return;
    }
    if (step === STEP.PROMO) {
      await api.updateOnboardingAnswers({ promoType: [...promoTypes] }).catch(() => {});
      setStep(STEP.BIO);
      return;
    }
    if (step === STEP.BIO) {
      setBusy(true);
      try {
        if (workspaceId) {
          await api.updateWorkspace(workspaceId, {
            profile: { currentSituation: (bio || '').trim() },
          });
        }
        setStep(STEP.INTRO);
      } catch (err) {
        setError(err?.message || 'Could not save bio');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === STEP.INTRO) {
      setStep(STEP.BROLL_KNOW);
      return;
    }
    if (step === STEP.BROLL_KNOW) {
      setStep(knowsBroll ? STEP.UPLOAD : STEP.BROLL_EXPLAIN);
      return;
    }
    if (step === STEP.BROLL_EXPLAIN) {
      setStep(STEP.UPLOAD);
      return;
    }
    if (step === STEP.UPLOAD) {
      if (localMoments.length) setStep(STEP.SELECT_CUTS);
      else fileInputRef.current?.click();
      return;
    }
    if (step === STEP.SELECT_CUTS) {
      const grouped = buildPendingCutWindowsByVideo(localMoments);
      const byUri = new Map();
      grouped.forEach((entry) => {
        byUri.set(String(entry.videoUri), {
          windows: entry.windows,
          videoId: entry.videoId,
        });
      });
      pendingCutWindowsByUriRef.current = byUri;

      if (workspaceId) {
        grouped.forEach((entry) => {
          const videoId = entry.videoId;
          if (!videoId || !entry.windows?.length) return;
          if (extractTriggeredVideoIdsRef.current.has(videoId)) return;
          extractTriggeredVideoIdsRef.current.add(videoId);
          api.extractWorkspaceCutWindows(workspaceId, videoId, entry.windows).catch((err) => {
            console.warn('[OnboardingOverlay] extract on Next failed', err);
            extractTriggeredVideoIdsRef.current.delete(videoId);
          });
        });

        if (!uploadStartedRef.current) {
          uploadStartedRef.current = true;
          startCutsPolling();
        }
      }
      setStep(STEP.TOPIC);
      return;
    }
    if (step === STEP.TOPIC) {
      await api.updateOnboardingAnswers({ videoTopic: topic }).catch(() => {});
      setStep(STEP.HAS_SCRIPT);
      return;
    }
    if (step === STEP.HAS_SCRIPT) {
      if (hasScript) {
        setStep(STEP.SCRIPT_PASTE);
      } else {
        setScript(null);
        setScriptContent('');
        setStep(STEP.SCRIPT_AI);
      }
      return;
    }
    if (step === STEP.SCRIPT_PASTE || step === STEP.SCRIPT_AI) {
      setBusy(true);
      try {
        await ensureScriptSaved();
        setStep(STEP.VOICE);
      } catch (err) {
        setError(err?.message || 'Could not save script');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === STEP.VOICE) {
      setStep(STEP.RESULT);
      return;
    }
    if (step === STEP.RESULT) {
      setStep(STEP.COMPLETE);
      return;
    }
    if (step === STEP.COMPLETE) {
      finish();
    }
  }, [
    step, referral, posting, promoTypes, bio, knowsBroll, localMoments, topic, hasScript,
    ensureScriptSaved, finish, onReferralSourceSaved, startCutsPolling, workspaceId,
  ]);

  const goBack = useCallback(() => {
    if (step === STEP.PROMO) { setStep(STEP.POSTING); return; }
    if (step === STEP.UPLOAD) {
      setStep(knowsBroll === false ? STEP.BROLL_EXPLAIN : STEP.BROLL_KNOW);
      return;
    }
    if (step === STEP.SCRIPT_PASTE || step === STEP.SCRIPT_AI) {
      setStep(STEP.HAS_SCRIPT);
      return;
    }
    if (step === STEP.VOICE) {
      setStep(hasScript ? STEP.SCRIPT_PASTE : STEP.SCRIPT_AI);
      return;
    }
    if (step > 0) setStep((s) => s - 1);
  }, [step, knowsBroll, hasScript]);

  if (!open) return null;

  const btnPrimary = {
    padding: '12px 24px',
    background: '#e1f679',
    color: '#000',
    fontWeight: 700,
    border: 'none',
    borderRadius: 8,
    cursor: canNext && !busy ? 'pointer' : 'not-allowed',
    opacity: canNext && !busy ? 1 : 0.5,
    fontSize: 15,
  };
  const btnGhost = {
    padding: '8px 0',
    background: 'transparent',
    color: muted,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    marginBottom: 12,
  };
  const choice = (selected) => ({
    padding: '14px 16px',
    background: selected ? '#e1f679' : (darkMode ? '#1c2436' : '#f1f5f9'),
    color: selected ? '#000' : text,
    border: `2px solid ${selected ? '#e1f679' : border}`,
    borderRadius: 8,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    marginBottom: 8,
    fontSize: 15,
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        background: darkMode ? 'rgba(6,8,14,0.85)' : 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          background: panel,
          color: text,
          borderRadius: 12,
          padding: '28px 24px',
          maxWidth: 640,
          width: '100%',
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          border: `1px solid ${border}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {step > STEP.REFERRAL && (
          <button type="button" style={btnGhost} onClick={goBack}>← Back</button>
        )}

        {step === STEP.REFERRAL && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22, textAlign: 'center' }}>How did you discover Merra?</h1>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {REFERRAL_OPTIONS.map((opt) => (
                <button key={opt} type="button" style={choice(referral === opt)} onClick={() => setReferral(opt)}>
                  {opt}
                </button>
              ))}
            </div>
          </>
        )}

        {step === STEP.POSTING && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>How many times a week do you want to post on social media?</h1>
            {POSTING_OPTIONS.map((opt) => (
              <button key={opt} type="button" style={choice(posting === opt)} onClick={() => setPosting(opt)}>
                {opt}
              </button>
            ))}
          </>
        )}

        {step === STEP.PROMO && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Let's build your profile and make a video</h1>
            <p style={{ color: muted }}>What do you want to promote? Select all that apply.</p>
            {PROMO_OPTIONS.map((opt) => {
              const selected = promoTypes.has(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  style={{
                    ...choice(selected),
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                  }}
                  onClick={() => {
                    setPromoTypes((prev) => {
                      const next = new Set(prev);
                      if (next.has(opt.id)) next.delete(opt.id);
                      else next.add(opt.id);
                      return next;
                    });
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      marginTop: 1,
                      borderRadius: 5,
                      border: selected ? '2px solid #000' : `2px solid ${border}`,
                      background: selected ? '#000' : 'transparent',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    {selected ? (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M3 7.2L5.8 10L11 3.5"
                          stroke="#e1f679"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: 'block' }}>{opt.label}</strong>
                    <span style={{ display: 'block', fontSize: 13, opacity: 0.75 }}>{opt.subtitle}</span>
                  </span>
                </button>
              );
            })}
          </>
        )}

        {step === STEP.BIO && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>
              {promoTypes.has('personal_brand') && promoTypes.has('brand')
                ? 'Tell us about you and the brand'
                : promoTypes.has('brand')
                  ? 'Tell us about the brand'
                  : 'Tell us about yourself'}
            </h1>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              placeholder="What is your account about?"
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${border}`,
                background: bg,
                color: text,
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />
          </>
        )}

        {step === STEP.INTRO && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Let's make a minivlog in 5 minutes</h1>
            <p style={{ color: muted }}>We need a voiceover and b-rolls.</p>
          </>
        )}

        {step === STEP.BROLL_KNOW && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Do you know what a b-roll is?</h1>
            <button type="button" style={choice(knowsBroll === true)} onClick={() => setKnowsBroll(true)}>Yes</button>
            <button type="button" style={choice(knowsBroll === false)} onClick={() => setKnowsBroll(false)}>No</button>
          </>
        )}

        {step === STEP.BROLL_EXPLAIN && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>What are b-rolls?</h1>
            <p style={{ color: muted }}>
              B-rolls are short, visual clips that play while your voiceover talks. They make your video feel dynamic without you on camera the whole time.
            </p>
            <ul style={{ color: muted, paddingLeft: 18 }}>
              {B_ROLL_EXAMPLES.map((ex) => <li key={ex}>{ex}</li>)}
            </ul>
          </>
        )}

        {step === STEP.UPLOAD && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Upload your b-rolls</h1>
            <p style={{ color: muted }}>Pick clips from your computer. Upload continues in the background.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              multiple
              style={{ display: 'none' }}
              onChange={handlePickFiles}
            />
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0 8px' }}>
              <ImportStackButton
                size="hero"
                label="Import videos"
                onClick={() => fileInputRef.current?.click()}
              />
            </div>
          </>
        )}

        {step === STEP.SELECT_CUTS && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Select cuts you want to keep</h1>
            <p style={{ color: muted }}>Smart 2s moments from your files. Hide any you don't want — we'll create those cuts once each upload finishes.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              multiple
              style={{ display: 'none' }}
              onChange={handlePickFiles}
            />
            {localMoments.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <ImportStackButton
                  size="hero"
                  label="Import videos"
                  onClick={() => fileInputRef.current?.click()}
                />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div
                  style={{
                    aspectRatio: '9/16',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                  }}
                >
                  <ImportStackButton
                    size="grid"
                    label="Import"
                    onClick={() => fileInputRef.current?.click()}
                  />
                </div>
                {localMoments.map((m) => (
                  <div
                    key={m.clientId}
                    style={{
                      position: 'relative',
                      aspectRatio: '9/16',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: `2px solid ${m.hidden ? '#dc3545' : border}`,
                      background: '#000',
                    }}
                  >
                    <MomentVideo moment={m} />
                    <button
                      type="button"
                      onClick={() =>
                        setLocalMoments((prev) => toggleLocalMomentHidden(prev, m.clientId))
                      }
                      style={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        right: 8,
                        padding: '6px 8px',
                        border: 'none',
                        borderRadius: 6,
                        background: m.hidden ? '#28a745' : 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {m.hidden ? 'Show' : 'Hide'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {step === STEP.TOPIC && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Enter a topic for the video</h1>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={3}
              placeholder="e.g. morning routine tips…"
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${border}`,
                background: bg,
                color: text,
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />
          </>
        )}

        {step === STEP.HAS_SCRIPT && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Do you have a script?</h1>
            <button type="button" style={choice(hasScript === true)} onClick={() => setHasScript(true)}>
              Yes — paste or type it
            </button>
            <button type="button" style={choice(hasScript === false)} onClick={() => setHasScript(false)}>
              No — generate with AI
            </button>
          </>
        )}

        {step === STEP.SCRIPT_PASTE && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Paste or type your script</h1>
            <textarea
              value={scriptContent}
              onChange={(e) => setScriptContent(e.target.value)}
              rows={8}
              placeholder="Hook… body… CTA"
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${border}`,
                background: bg,
                color: text,
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />
          </>
        )}

        {step === STEP.SCRIPT_AI && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Here's your script</h1>
            <p style={{ color: muted }}>
              AI generated versions based on your topic and bio. Pick one — hook, setup, body, and closing are highlighted.
            </p>
            {generatingScript ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  padding: '36px 16px',
                  marginBottom: 16,
                  borderRadius: 8,
                  border: `1px solid ${border}`,
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)',
                }}
              >
                <span className="spinner-border" style={{ width: 28, height: 28, borderWidth: 3 }} />
                <p style={{ margin: 0, color: muted }}>Generating your script…</p>
              </div>
            ) : (
              <>
                {(script?.contentAlt || script?.contentThird) && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    {['main', 'alt', 'third'].map((v, i) => (
                      <button
                        key={v}
                        type="button"
                        style={{ ...choice(scriptVersion === v), width: 'auto', marginBottom: 0 }}
                        onClick={() => {
                          setScriptVersion(v);
                          const next =
                            v === 'alt' ? (script.contentAlt || script.content)
                              : v === 'third' ? (script.contentThird || script.content)
                                : script.content;
                          setScriptContent(next || '');
                          setIsEditingScript(false);
                        }}
                      >
                        Version {['A', 'B', 'C'][i]}
                      </button>
                    ))}
                  </div>
                )}
                {!isEditingScript ? (
                  <>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setIsEditingScript(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsEditingScript(true);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: 12,
                        borderRadius: 8,
                        border: `1px solid ${border}`,
                        background: bg,
                        color: text,
                        marginBottom: 8,
                        boxSizing: 'border-box',
                        lineHeight: 1.6,
                        fontSize: 14,
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        cursor: 'text',
                        minHeight: 140,
                      }}
                    >
                      {(() => {
                        const textValue = scriptContent || script?.content || '';
                        const groups = getScriptSentenceGroups(textValue);
                        if (groups.length === 0) return textValue || <span style={{ color: muted }}>Your script will appear here…</span>;
                        return groups.map((group, index) => {
                          const sentence = group.join('');
                          const isFirst = index === 0;
                          const isSecond = index === 1;
                          const isLast = index === groups.length - 1;
                          let bgColor;
                          let glowColor;
                          if (isFirst) {
                            bgColor = darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)';
                            glowColor = 'rgba(239, 68, 68, 0.4)';
                          } else if (isSecond) {
                            bgColor = darkMode ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)';
                            glowColor = 'rgba(234, 179, 8, 0.4)';
                          } else if (isLast) {
                            bgColor = darkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)';
                            glowColor = 'rgba(34, 197, 94, 0.4)';
                          } else {
                            bgColor = darkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)';
                            glowColor = 'rgba(59, 130, 246, 0.4)';
                          }
                          return (
                            <span
                              key={index}
                              style={{
                                backgroundColor: bgColor,
                                padding: '2px 4px',
                                borderRadius: 4,
                                boxShadow: `0 0 8px ${glowColor}`,
                                display: 'inline',
                              }}
                            >
                              {sentence}
                            </span>
                          );
                        });
                      })()}
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsEditingScript(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: muted,
                        cursor: 'pointer',
                        fontSize: 13,
                        padding: 0,
                        marginBottom: 10,
                      }}
                    >
                      tap to edit
                    </button>
                  </>
                ) : (
                  <textarea
                    value={scriptContent}
                    onChange={(e) => setScriptContent(e.target.value)}
                    onBlur={() => setIsEditingScript(false)}
                    autoFocus
                    rows={8}
                    style={{
                      width: '100%',
                      padding: 12,
                      borderRadius: 8,
                      border: '2px solid #8b5cf6',
                      background: bg,
                      color: text,
                      marginBottom: 16,
                      boxSizing: 'border-box',
                      lineHeight: 1.6,
                      fontSize: 14,
                      fontFamily: 'inherit',
                    }}
                  />
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    marginBottom: 16,
                    fontSize: 12,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {[
                    { label: 'hook', bg: darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)', glow: 'rgba(239, 68, 68, 0.4)' },
                    { label: 'setup', bg: darkMode ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.15)', glow: 'rgba(234, 179, 8, 0.4)' },
                    { label: 'body', bg: darkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)', glow: 'rgba(59, 130, 246, 0.4)' },
                    { label: 'closing', bg: darkMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)', glow: 'rgba(34, 197, 94, 0.4)' },
                  ].map((item) => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          borderRadius: 4,
                          backgroundColor: item.bg,
                          boxShadow: `0 0 8px ${item.glow}`,
                        }}
                      />
                      <span style={{ color: muted }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {step === STEP.VOICE && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Generate your voiceover</h1>
            <p style={{ color: muted }}>We'll use Brian's voice. You can clone yours later in Studio.</p>
            {voiceoverUrl ? (
              <audio controls src={voiceoverUrl} style={{ width: '100%', marginBottom: 16 }} />
            ) : (
              <button
                type="button"
                onClick={generateVoiceover}
                disabled={generatingVo || !(scriptContent || '').trim()}
                style={{
                  padding: '12px 24px',
                  background: '#e1f679',
                  color: '#000',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: 8,
                  cursor: generatingVo || !(scriptContent || '').trim() ? 'not-allowed' : 'pointer',
                  opacity: generatingVo || !(scriptContent || '').trim() ? 0.7 : 1,
                  fontSize: 15,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  marginBottom: 16,
                }}
              >
                {generatingVo ? (
                  <>
                    <span className="spinner-border" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    Generating…
                  </>
                ) : (
                  "Generate with Brian's voice"
                )}
              </button>
            )}
          </>
        )}

        {step === STEP.RESULT && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>Your minivlog</h1>
            <p style={{ color: muted }}>
              {generatedVideoUrl && !generatingVideo
                ? 'Preview your video, or open the timeline to tweak cuts and subtitles.'
                : 'Creating your video…'}
            </p>

            {(generatingVideo || (videoProgress > 0 && videoProgress < 100 && !generatedVideoUrl)) && (
              <div style={{ marginBottom: 16 }}>
                <VideoGenerationSteps
                  progress={videoProgress}
                  perStepProgress
                  showProgressBar
                  shimmer
                />
                {cuts.filter((c) => !c.hidden && c.url).length > 0 ? (
                  <div style={{ marginTop: 8, marginBottom: 8, height: 260 }}>
                    <SmoothScaleCarousel
                      items={cuts.filter((c) => !c.hidden && c.url).slice(0, 15)}
                      compact
                    />
                  </div>
                ) : (
                  <p style={{ color: muted, fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                    Preparing your clips…
                  </p>
                )}
              </div>
            )}

            {generatedVideoUrl && !generatingVideo && (
              <>
                <video
                  src={generatedVideoUrl}
                  controls
                  playsInline
                  style={{ width: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 420, background: '#000' }}
                />
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={downloadOnboardingVideo}
                    style={{
                      padding: '12px 24px',
                      background: '#e1f679',
                      color: '#000',
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 15,
                      flex: 1,
                    }}
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '12px 24px',
                      background: darkMode ? '#1c2436' : '#e2e8f0',
                      color: text,
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 15,
                      flex: 1,
                    }}
                    onClick={() => openOnboardingNleEditor()}
                  >
                    Edit timeline
                  </button>
                </div>
                {videoSaved || savingVideo ? (
                  <p style={{ color: muted, fontSize: 13, marginTop: 0 }}>
                    {savingVideo ? 'Saving to My Generated Videos…' : 'Saved to My Generated Videos'}
                  </p>
                ) : null}
              </>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              {!generatingVideo && !generatedVideoUrl && (
                <button
                  type="button"
                  onClick={() => {
                    videoGenStartedRef.current = false;
                    generateVideo();
                  }}
                  disabled={!script?._id || !voiceoverUrl}
                  style={{
                    padding: '12px 24px',
                    background: '#e1f679',
                    color: '#000',
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: 8,
                    cursor: !script?._id || !voiceoverUrl ? 'not-allowed' : 'pointer',
                    opacity: !script?._id || !voiceoverUrl ? 0.5 : 1,
                    fontSize: 15,
                    flex: 1,
                  }}
                >
                  Generate video
                </button>
              )}
              {!generatingVideo && generatedVideoUrl && (
                <button
                  type="button"
                  onClick={() => {
                    videoGenStartedRef.current = false;
                    generateVideo();
                  }}
                  disabled={!script?._id || !voiceoverUrl}
                  style={{
                    padding: '12px 24px',
                    background: darkMode ? '#1c2436' : '#e2e8f0',
                    color: text,
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: 8,
                    cursor: !script?._id || !voiceoverUrl ? 'not-allowed' : 'pointer',
                    opacity: !script?._id || !voiceoverUrl ? 0.5 : 1,
                    fontSize: 15,
                    flex: 1,
                  }}
                >
                  Regenerate video
                </button>
              )}
            </div>
          </>
        )}

        {step === STEP.COMPLETE && (
          <>
            <h1 style={{ marginTop: 0, fontSize: 22 }}>And that's how you create mini vlogs super fast!</h1>
            <p style={{ color: muted }}>
              You can keep uploading b-rolls, tweaking scripts, and exporting from Studio anytime.
            </p>
          </>
        )}

        {error ? <p style={{ color: '#f87171', fontSize: 13 }}>{error}</p> : null}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: step === STEP.BROLL_EXPLAIN ? 32 : 16 }}>
          <button type="button" style={btnPrimary} disabled={!canNext || busy} onClick={goNext}>
            {busy ? '…' : step === STEP.COMPLETE ? 'Close tutorial' : step === STEP.BROLL_EXPLAIN ? 'Got it — upload b-rolls' : 'Continue'}
          </button>
        </div>
      </div>

      {showNle && (
        <NewNleEditor
          open={showNle}
          onClose={() => setShowNle(false)}
          darkMode={darkMode}
          voiceoverUrl={voiceoverUrl || script?.voiceoverUrl}
          voiceoverTimestamps={voiceoverTimestamps || script?.voiceoverTimestamps || null}
          scriptContent={scriptContent || script?.content || ''}
          initialCuts={nleTimelineCuts}
          availableCuts={cuts}
          loading={nleCutsLoading}
          workspaceId={workspaceId}
          enableSubtitles
          oneWordPerSubtitle={false}
          exporting={generatingVideo}
          onExport={() => {
            setShowNle(false);
            videoGenStartedRef.current = false;
            generateVideo();
          }}
        />
      )}
    </div>
  );
}
